import React, { useContext, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useGetScenarios } from 'components/test-suites/forms/use-get-scenarios'
import {
  getCanCreateAndEditTestSuites,
  isGremlinOfficialTestSuite
} from 'components/test-suites/test-suites-utils'
import useAuth from 'hooks/use-auth'
import { useGetTestSuite } from 'queries/test-suites/use-get-test-suite'
import { useGetTestSuitesDetectedRisks } from 'queries/test-suites/use-get-test-suites-detected-risks'
import {
  type RecommendedScenario,
  type SharedScenario,
  type Scenario
} from 'types/api/scenarios/scenario'
import { type TestSuite } from 'types/api/test-suites/test-suites'
import { createLookupMapFromArray } from 'utils/obj'

export type ScenarioForTestSuite =
  | Scenario
  | SharedScenario
  | RecommendedScenario

export enum TestSuiteFormMode {
  Clone = 'Clone',
  Edit = 'Edit',
  New = 'New'
}

export interface TestSuiteFormContextValues {
  canCreateAndEditTestSuites: boolean
  isErrorScenarios: boolean
  isLoadingScenarios: boolean
  isOfficialTestSuite: boolean
  scenariosAggregated: ScenarioForTestSuite[]
  scenariosLookup: Record<string, ScenarioForTestSuite>
  queryResultTestSuite: ReturnType<typeof useGetTestSuite>
  queryResultTestSuitesDetectedRisks: ReturnType<
    typeof useGetTestSuitesDetectedRisks
  >
  testSuiteClone: TestSuite | undefined
  testSuiteToEdit: TestSuite | undefined
  testSuiteFormMode: TestSuiteFormMode
}

export const TestSuiteFormContext = React.createContext<
  TestSuiteFormContextValues | undefined
>(undefined)

export function useTestSuiteFormContext() {
  const context = useContext(TestSuiteFormContext)

  if (!context) {
    throw new Error(
      'useTestSuiteFormContext must be used within a TestSuiteFormContextProvider'
    )
  }

  return context
}

export const TestSuiteFormContextProvider: React.FC<{
  isEdit?: boolean
}> = ({ children, isEdit = false }) => {
  const { hasEndpointAccess } = useAuth()
  const canCreateAndEditTestSuites =
    getCanCreateAndEditTestSuites(hasEndpointAccess)

  const location = useLocation()

  // Create mode can be 'new' or 'clone'
  const testSuite: TestSuite | undefined = location?.state?.testSuite
  const isClone = isEdit ? false : Boolean(testSuite)

  // isEdit will have a testSuiteId in the route
  const { testSuiteId } = useParams()

  const queryResultTestSuitesDetectedRisks = useGetTestSuitesDetectedRisks()

  const queryResultTestSuite = useGetTestSuite(testSuiteId, {
    enabled: Boolean(isEdit && testSuiteId),
    placeholderData: location.state?.testSuite as TestSuite
  })

  const isOfficialTestSuite =
    isEdit && queryResultTestSuite.data
      ? isGremlinOfficialTestSuite(queryResultTestSuite.data)
      : false

  const {
    publishedScenarios,
    isFetchingPublishedScenarios,
    hasErrorPublishedScenarios,
    draftScenarios,
    isFetchingDraftScenarios,
    hasErrorDraftScenarios,
    sharedScenarios,
    isFetchingSharedScenarios,
    hasErrorSharedScenarios,
    recommendedScenarios,
    isFetchingRecommendedScenarios,
    hasErrorRecommendedScenarios
  } = useGetScenarios()

  const isLoadingScenarios =
    isFetchingPublishedScenarios ||
    isFetchingDraftScenarios ||
    isFetchingSharedScenarios ||
    isFetchingRecommendedScenarios

  const isErrorScenarios =
    hasErrorPublishedScenarios ||
    hasErrorDraftScenarios ||
    hasErrorSharedScenarios ||
    hasErrorRecommendedScenarios

  const scenariosAggregated = useMemo(() => {
    if (isLoadingScenarios || isErrorScenarios) {
      return []
    }

    const aggregatedScenarios = [
      publishedScenarios,
      draftScenarios,
      sharedScenarios ?? [],
      recommendedScenarios ?? []
    ]

    const guids = new Set<string>()
    const dedupedScenarios: ScenarioForTestSuite[] = []

    for (const scenarioList of aggregatedScenarios) {
      for (const scenario of scenarioList) {
        if (!guids.has(scenario.guid)) {
          guids.add(scenario.guid)
          dedupedScenarios.push(scenario)
        }
      }
    }

    return dedupedScenarios
  }, [
    draftScenarios,
    isErrorScenarios,
    isLoadingScenarios,
    publishedScenarios,
    recommendedScenarios,
    sharedScenarios
  ])

  const scenariosLookup = useMemo(
    () =>
      createLookupMapFromArray<ScenarioForTestSuite>(
        scenariosAggregated,
        'guid'
      ),
    [scenariosAggregated]
  )

  const testSuiteFormMode: TestSuiteFormMode = useMemo(() => {
    if (isClone) return TestSuiteFormMode.Clone
    return isEdit ? TestSuiteFormMode.Edit : TestSuiteFormMode.New
  }, [isClone, isEdit])

  const contextValue = useMemo(
    () => ({
      canCreateAndEditTestSuites,
      isErrorScenarios,
      isLoadingScenarios,
      isOfficialTestSuite,
      scenariosAggregated,
      scenariosLookup,
      queryResultTestSuite,
      queryResultTestSuitesDetectedRisks,
      testSuiteClone:
        testSuiteFormMode === TestSuiteFormMode.Clone && testSuite
          ? testSuite
          : undefined,
      testSuiteToEdit:
        testSuiteFormMode === TestSuiteFormMode.Edit &&
        queryResultTestSuite.data
          ? queryResultTestSuite.data
          : undefined,
      testSuiteFormMode
    }),
    [
      canCreateAndEditTestSuites,
      isErrorScenarios,
      isLoadingScenarios,
      isOfficialTestSuite,
      scenariosAggregated,
      scenariosLookup,
      queryResultTestSuite,
      queryResultTestSuitesDetectedRisks,
      testSuite,
      testSuiteFormMode
    ]
  )

  return (
    <TestSuiteFormContext.Provider value={contextValue}>
      {children}
    </TestSuiteFormContext.Provider>
  )
}
